import { PageTitle } from 'components/PageTitle'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useEffect, type ReactNode } from 'react'

type HelpContent = ReturnType<
  typeof useTranslationTyped
>['tt']['help']['canvas']

/**
 * Adds `<meta name="robots" content="noindex">` while mounted so search
 * engines skip these unlisted pages. Each mount owns and removes its own tag,
 * so the order of mount/unmount during page transitions does not matter.
 */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex'
    document.head.appendChild(meta)
    return () => meta.remove()
  }, [])
}

/**
 * Shared, unlisted documentation page describing the LTI custom field mappings
 * required by Breakout Learning and how to update them in the LMS. Rendered by
 * the Canvas and D2L wrappers; reachable by URL but not linked from navigation.
 */
export const LtiFieldMappingHelpPage = observer(
  function LtiFieldMappingHelpPage({
    content,
    steps,
  }: {
    content: HelpContent
    steps: ReactNode
  }) {
    const { tt } = useTranslationTyped()
    useNoIndex()

    return (
      <main className="flex min-h-full w-full flex-col overflow-auto bg-surface-bright">
        <PageTitle title={tt.help.page_title()} />
        <div className="mx-auto w-full max-w-[720px] px-6 py-16">
          <div className="mb-8 flex items-start justify-between gap-6">
            <h1
              className="text-headline-large"
              data-testid="lti-field-mapping-help-title"
            >
              {content.title()}
            </h1>
            <img
              src="/assets/images/logo.png"
              width={64}
              className="shrink-0"
              alt={tt.breakout_learning()}
            />
          </div>

          <h2 className="mb-2 text-title-large">
            {content.overview_heading()}
          </h2>
          <p className="mb-8 text-body-large text-on-surface-var">
            {content.overview_body()}
          </p>

          <h2 className="mb-2 text-title-large">{content.update_heading()}</h2>
          <div className="mb-8 text-body-large text-on-surface-var">
            {steps}
          </div>

          <h2 className="mb-2 text-title-large">{content.fields_heading()}</h2>
          <pre
            tabIndex={0}
            role="region"
            aria-label={content.fields_heading()}
            data-testid="lti-field-mapping-help-fields"
            className="mb-8 overflow-auto rounded-2xl border border-outline-medium bg-core-tertiary p-5"
          >
            <code className="text-body-medium whitespace-pre">
              {content.fields()}
            </code>
          </pre>

          <h2 className="mb-2 text-title-large">{content.verify_heading()}</h2>
          <p className="mb-8 text-body-large text-on-surface-var">
            {content.verify_body()}
          </p>

          <h2 className="mb-2 text-title-large">
            {content.need_help_heading()}
          </h2>
          <p className="text-body-large text-on-surface-var">
            {content.need_help_body()}
          </p>
        </div>
      </main>
    )
  }
)
